import { typedSupabase } from "@/lib/typed-supabase";
import {
  IService,
  ServiceResponse,
  createErrorResponse,
  createServiceError,
} from "@/services/core/service-registry";
import type { TimeBlock } from "@/types/time-block";
import { logger } from "@/lib/logger";

export interface ITimeBlockService extends IService {
  getTimeBlocks(userId?: string): Promise<ServiceResponse<TimeBlock[]>>;
  createTimeBlock(
    timeBlock: Omit<TimeBlock, "id" | "created_at" | "updated_at">
  ): Promise<ServiceResponse<TimeBlock>>;
  updateTimeBlock(
    id: string,
    updates: Partial<TimeBlock>
  ): Promise<ServiceResponse<TimeBlock>>;
  deleteTimeBlock(id: string): Promise<ServiceResponse<void>>;
}

export class TimeBlockService implements ITimeBlockService {
  public readonly name = "TimeBlockService" as const;

  async initialize(): Promise<void> {
    logger.info("TimeBlockService initialized", { service: this.name });
  }

  async dispose(): Promise<void> {
    logger.info("TimeBlockService disposed", { service: this.name });
  }

  async getTimeBlocks(userId?: string): Promise<ServiceResponse<TimeBlock[]>> {
    try {
      // Build query - if userId is not provided or is "all", get all time blocks
      // Select only essential columns to reduce payload size
      let query = typedSupabase.from("time_blocks").select(`
        id,
        title,
        description,
        start_time,
        end_time,
        task_ids,
        block_type,
        calendar_event_id,
        color,
        recurrence_pattern_id,
        user_id,
        is_locked,
        created_at,
        updated_at
      `);

      if (userId && userId !== "all") {
        query = query.eq("user_id", userId);
      }

      const { data, error } = await query;

      if (error) {
        logger.error("Error fetching time blocks", { error: error.message, userId });
        return createErrorResponse(
          createServiceError("FETCH_TIME_BLOCKS_ERROR", error.message, this.name, { userId })
        );
      }

      // Map null fields to undefined for each time block
      const mappedData = (data || []).map((block) => ({
        ...block,
        description: block.description ?? undefined,
        color: block.color ?? undefined,
        calendar_event_id: block.calendar_event_id ?? undefined,
        recurrence_pattern_id: block.recurrence_pattern_id ?? undefined,
        created_at: block.created_at ?? undefined,
        updated_at: block.updated_at ?? undefined,
        is_locked: block.is_locked ?? undefined,
      })) as TimeBlock[];

      return { success: true, data: mappedData };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch time blocks";
      logger.error("Exception in getTimeBlocks", { error: errorMessage, userId });
      return createErrorResponse(
        createServiceError("FETCH_TIME_BLOCKS_ERROR", errorMessage, this.name, { error })
      );
    }
  }

  /**
   * @deprecated Browser writes to `time_blocks` must go through the TanStack
   * DB collection path (`useTimeBlocks` / `getTimeBlocksCollection`).
   * Retained for server-side and integration callers.
   */
  async createTimeBlock(
    timeBlock: Omit<TimeBlock, "id" | "created_at" | "updated_at">
  ): Promise<ServiceResponse<TimeBlock>> {
    try {
      const { data, error } = await typedSupabase
        .from("time_blocks")
        .insert(timeBlock)
        .select()
        .single();

      if (error) {
        logger.error("Error creating time block", { error: error.message });
        return createErrorResponse(
          createServiceError("CREATE_TIME_BLOCK_ERROR", error.message, this.name)
        );
      }

      // Map null fields to undefined to match TimeBlock type
      const mappedData = {
        ...data,
        description: data?.description ?? undefined,
        color: data?.color ?? undefined,
        calendar_event_id: data?.calendar_event_id ?? undefined,
        recurrence_pattern_id: data?.recurrence_pattern_id ?? undefined,
        created_at: data?.created_at ?? undefined,
        updated_at: data?.updated_at ?? undefined,
        is_locked: data?.is_locked ?? undefined,
      } as TimeBlock;
      return { success: true, data: mappedData };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to create time block";
      logger.error("Exception in createTimeBlock", { error: errorMessage });
      return createErrorResponse(
        createServiceError("CREATE_TIME_BLOCK_ERROR", errorMessage, this.name, { error })
      );
    }
  }

  /**
   * @deprecated Browser writes to `time_blocks` must go through the TanStack
   * DB collection path (`useTimeBlocks` / `getTimeBlocksCollection`).
   * Retained for server-side and integration callers.
   */
  async updateTimeBlock(
    id: string,
    updates: Partial<TimeBlock>
  ): Promise<ServiceResponse<TimeBlock>> {
    try {
      const { data, error } = await typedSupabase
        .from("time_blocks")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        logger.error("Error updating time block", { error: error.message, id });
        return createErrorResponse(
          createServiceError("UPDATE_TIME_BLOCK_ERROR", error.message, this.name, { id })
        );
      }

      // Map null fields to undefined to match TimeBlock type
      const mappedData = {
        ...data,
        description: data?.description ?? undefined,
        color: data?.color ?? undefined,
        calendar_event_id: data?.calendar_event_id ?? undefined,
        recurrence_pattern_id: data?.recurrence_pattern_id ?? undefined,
        created_at: data?.created_at ?? undefined,
        updated_at: data?.updated_at ?? undefined,
        is_locked: data?.is_locked ?? undefined,
      } as TimeBlock;

      return { success: true, data: mappedData };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update time block";
      logger.error("Exception in updateTimeBlock", { error: errorMessage, id });
      return createErrorResponse(
        createServiceError("UPDATE_TIME_BLOCK_ERROR", errorMessage, this.name, { id, error })
      );
    }
  }

  /**
   * @deprecated Browser writes to `time_blocks` must go through the TanStack
   * DB collection path (`useTimeBlocks` / `getTimeBlocksCollection`).
   * Retained for server-side and integration callers.
   */
  async deleteTimeBlock(id: string): Promise<ServiceResponse<void>> {
    try {
      const { error } = await typedSupabase
        .from("time_blocks")
        .delete()
        .eq("id", id);

      if (error) {
        logger.error("Error deleting time block", { error: error.message, id });
        return createErrorResponse(
          createServiceError("DELETE_TIME_BLOCK_ERROR", error.message, this.name, { id })
        );
      }

      return { success: true, data: undefined };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to delete time block";
      logger.error("Exception in deleteTimeBlock", { error: errorMessage, id });
      return createErrorResponse(
        createServiceError("DELETE_TIME_BLOCK_ERROR", errorMessage, this.name, { id, error })
      );
    }
  }
}
